// Screens students (and teachers) use: sign in, home, a batch, a class, profile.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link } from '../router.js';
import { useApp, isStandalone, isIOS, opensInNewTab } from '../state.js';
import { config, googleSignInOn, reviewSignInOn, rpc, sessionProvider, signIn, signInWithPassword } from '../api.js';
import { Icon } from '../ui/icons.js';
import { Brand, Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Notice, Progress, Section, useRpc, useToast } from '../ui/components.jsx';
import { NextClass } from './timetable.jsx';
import { FeesLink } from './fees.jsx';
import { TestCard } from './tests.jsx';
import { currentSubscription, disableNotifications, enableNotifications, permission, pushSupport } from '../push.js';
import { ReminderPrompt } from '../ui/reminder-prompt.jsx';
import { SharingRequest } from './parents.jsx';
import { dateLong, dateShort, dateTile, daysUntil, duration, firstName, greeting, msUrl, plural } from '../format.js';

const waLink = (text) => `https://wa.me/${config.whatsapp}?text=${encodeURIComponent(text)}`;

/* ---------------------------------------------------------------- Sign in */
export function SignIn({ error, onSignedIn }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(error || null);
  const [google, setGoogle] = useState(false);
  const [review, setReview] = useState(false); // the Google Play review sign-in is switched on
  const [reviewOpen, setReviewOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [reviewErr, setReviewErr] = useState(null);
  useEffect(() => {
    let live = true;
    googleSignInOn().then((on) => { if (live) setGoogle(on); });
    reviewSignInOn().then((on) => { if (live) setReview(on); });
    return () => { live = false; };
  }, []);
  const go = async (provider) => {
    setBusy(provider);
    setErr(null);
    try { await signIn(location.pathname + location.search, provider); } catch (e) { setErr(e.message); setBusy(''); }
  };
  const reviewSignIn = async (e) => {
    e.preventDefault();
    setBusy('email');
    setReviewErr(null);
    try {
      await signInWithPassword(form.email, form.password);
      if (onSignedIn) onSignedIn();
    } catch (ex) {
      setReviewErr(ex.message);
      setBusy('');
    }
  };
  return (
    <div class="signin">
      <div class="signin-rings" aria-hidden="true"><span /><span /><span /></div>
      <main class="signin-card" id="content">
        <Brand />
        <h1>Your ACCA classes, recordings and notes in one place</h1>
        <p class="signin-lede">Sign in with the Microsoft account the academy set up for you. Its username looks like this:</p>
        <p class="signin-example">name@vidyavedhacademyllp.onmicrosoft.com</p>
        {err ? <div class="notice notice-bad" role="alert"><Icon name="alert" /><div><p>{err}</p></div></div> : null}
        <Button variant="ms" size="lg" busy={busy === 'azure'} disabled={Boolean(busy)} onClick={() => go('azure')} class="signin-btn">
          <span class="ms-logo" aria-hidden="true"><i /><i /><i /><i /></span>Sign in with Microsoft
        </Button>
        {google ? (
          <div class="signin-parent">
            <p><strong>Parent or guardian?</strong> Sign in with the Google account for the email you gave the academy.</p>
            <Button variant="line" busy={busy === 'google'} disabled={Boolean(busy)} icon="family" onClick={() => go('google')} class="signin-btn">Parents: sign in with Google</Button>
          </div>
        ) : null}
        <p class="signin-help">Trouble signing in? <a href={waLink('Hi Vidyavedh Academy, I need help signing in to the learning app.')} target="_blank" rel="noopener">Message us on WhatsApp</a></p>
        {review && !reviewOpen ? (
          <p class="signin-help"><button type="button" class="signin-link" onClick={() => setReviewOpen(true)}>App review sign-in</button></p>
        ) : null}
        {review && reviewOpen ? (
          <form class="signin-review form" onSubmit={reviewSignIn} aria-labelledby="review-title">
            <h2 id="review-title">App review sign-in</h2>
            <p class="fine">Only for the review account the academy gave Google Play. Students and teachers sign in with Microsoft.</p>
            {reviewErr ? <div class="notice notice-bad" role="alert"><Icon name="alert" /><div><p>{reviewErr}</p></div></div> : null}
            <Field label="Email">{(id) => (
              <input id={id} type="email" autoComplete="username" required value={form.email} onInput={(e) => setForm({ ...form, email: e.currentTarget.value })} />
            )}</Field>
            <Field label="Password">{(id) => (
              <input id={id} type="password" autoComplete="current-password" required value={form.password} onInput={(e) => setForm({ ...form, password: e.currentTarget.value })} />
            )}</Field>
            <div class="form-actions">
              <Button type="submit" variant="navy" busy={busy === 'email'} disabled={Boolean(busy) && busy !== 'email'}>Sign in</Button>
              <Button variant="ghost" onClick={() => { setReviewOpen(false); setReviewErr(null); }}>Cancel</Button>
            </div>
          </form>
        ) : null}
        {/* The app's front page for Google's branding check too: it names the app exactly as the Google
            sign-in settings do, says what the app is for and links the privacy notice (public/index.html
            carries the same for readers without JavaScript). */}
        <section class="signin-about" aria-labelledby="signin-about-title">
          <h2 id="signin-about-title">About the Vidyavedh Learning App</h2>
          <p>Vidyavedh Academy LLP, Pune, runs this app for its ACCA students, their teachers and their parents.</p>
          <ul>
            <li><strong>Students</strong> see their class timetable, join live classes, watch class recordings, open the notes, take practice tests and mock exams, and send doubts to their teachers.</li>
            <li><strong>Teachers</strong> take attendance, set tests, mark mock exams and answer doubts.</li>
            <li><strong>Parents and guardians</strong> follow their child’s attendance, marks and fee receipts.</li>
          </ul>
          <p>Parents sign in with Google only so the app can confirm their email address. The privacy notice says what the app keeps and why.</p>
        </section>
        <p class="signin-help signin-privacy">
          <a href="/privacy">Privacy notice</a> · <a href="https://vidyavedh.com/terms" target="_blank" rel="noopener">Terms</a> · <a href="https://vidyavedh.com" target="_blank" rel="noopener">vidyavedh.com</a>
        </p>
      </main>
    </div>
  );
}

/* ---------------------------------------------------------------- Problem screens */
export function Problem({ error, onSignOut, onRetry }) {
  const [provider, setProvider] = useState(null);
  useEffect(() => { sessionProvider().then(setProvider).catch(() => {}); }, []);
  const blocked = error && error.reason === 'blocked';
  const unknown = error && error.reason === 'not_registered';
  const parent = unknown && provider === 'google';
  const wrong = error && error.reason === 'wrong_sign_in';
  const title = blocked ? 'Your account is paused' : wrong ? 'Your account needs the academy’s help'
    : parent ? 'This Google account isn’t linked yet' : unknown ? 'Your account isn’t set up yet' : 'We couldn’t open the app';
  return (
    <div class="signin">
      <main class="signin-card" id="content">
        <Brand />
        <h1>{title}</h1>
        {parent ? (
          <>
            <p class="signin-lede">Parents sign in with the Google account for the email they gave the academy. Ask the academy to add this email to your child, or sign out and choose another Google account.</p>
            <p class="signin-lede">Students and teachers: sign out and use <strong>Sign in with Microsoft</strong>.</p>
          </>
        ) : <p class="signin-lede">{(error && error.message) || 'Please try again in a moment.'}</p>}
        <div class="row">
          {onRetry ? <Button variant="navy" icon="refresh" onClick={onRetry}>Try again</Button> : null}
          <Button variant="line" icon="logout" onClick={onSignOut}>Sign out</Button>
        </div>
        <p class="signin-help"><a href={waLink(parent ? 'Hi Vidyavedh Academy, I am a parent and the learning app says my Google account isn’t linked yet.' : 'Hi Vidyavedh Academy, I can’t open the learning app.')} target="_blank" rel="noopener">Message us on WhatsApp</a></p>
      </main>
    </div>
  );
}

/* ---------------------------------------------------------------- Home */
function BatchCard({ b, me }) {
  const left = daysUntil(b.access_until);
  const ending = left !== null && left <= 14;
  const student = me.role === 'student';
  const teacher = b.teacher && b.teacher !== me.full_name ? ` · ${b.teacher}` : '';
  return (
    <Link href={`/batches/${b.id}`} class="batch-card">
      <div class="batch-card-top">
        <span class="subject">{b.subject || '•'}</span>
        <div class="batch-card-title">
          <h3>{b.name}</h3>
          <p>{plural(b.class_count, 'class', 'classes')}{teacher}</p>
        </div>
        {b.new_this_week ? <Chip tone="gold">{b.new_this_week} new</Chip> : null}
      </div>
      {!student && b.last_class_date ? (
        <p class="batch-card-until"><Icon name="calendar" />Latest class {dateShort(b.last_class_date)}</p>
      ) : null}
      {student && b.class_count ? (
        <div class="batch-card-progress">
          <Progress value={b.opened_count} max={b.class_count} label={`${b.opened_count} of ${b.class_count} classes opened`} />
          <span>{b.opened_count} of {b.class_count} opened</span>
        </div>
      ) : null}
      {b.access_until ? (
        <p class={`batch-card-until${ending ? ' is-ending' : ''}`}>
          <Icon name="clock" />{ending ? `Access ends ${left <= 0 ? 'today' : `in ${plural(left, 'day')}`} (${dateShort(b.access_until)})` : `Access until ${dateShort(b.access_until)}`}
        </p>
      ) : null}
    </Link>
  );
}

export function Home() {
  const { me, home, reloadHome } = useApp();
  useEffect(() => { reloadHome(true); }, []);
  const batches = (home && home.batches) || [];
  const regular = batches.filter((b) => b.kind !== 'revision');
  const revision = batches.filter((b) => b.kind === 'revision');
  const cont = home && home.continue;
  const next = home && home.next_session;
  const unread = (home && home.unread_updates) || 0;
  const tests = (home && home.tests_badge) || 0;
  const attendance = (home && home.attendance_due) || 0;
  return (
    <Shell>
      <div class="hello">
        <p class="hello-date">{dateLong(new Date().toISOString())}</p>
        <h1 class="page-title">{greeting()}, {firstName(me)}</h1>
      </div>
      {next ? <NextClass s={next} /> : null}
      {unread ? (
        <Link href="/updates" class="news">
          <span class="news-icon"><Icon name="megaphone" /></span>
          <span class="news-text"><strong>{unread === 1 ? '1 new update' : `${unread} new updates`}</strong><span>From the academy</span></span>
          <Icon name="next" />
        </Link>
      ) : null}
      {tests ? (
        <Link href="/tests" class="news news-tests">
          <span class="news-icon"><Icon name="tests" /></span>
          {me.role === 'student'
            ? <span class="news-text"><strong>{tests === 1 ? '1 test to do' : `${tests} tests to do`}</strong><span>Practice tests and mock exams from your teachers</span></span>
            : <span class="news-text"><strong>{tests === 1 ? '1 script to mark' : `${tests} scripts to mark`}</strong><span>Mock exams waiting for your marks</span></span>}
          <Icon name="next" />
        </Link>
      ) : null}
      {attendance ? (
        <Link href="/attendance" class="news news-attendance">
          <span class="news-icon"><Icon name="users" /></span>
          <span class="news-text"><strong>{attendance === 1 ? '1 class needs attendance' : `${attendance} classes need attendance`}</strong><span>Tick who was there, online or offline</span></span>
          <Icon name="next" />
        </Link>
      ) : null}
      {me.role === 'student' ? <SharingRequest n={(home && home.sharing_requests) || 0} /> : null}
      {batches.length ? <ReminderPrompt /> : null}
      {cont ? (
        <Link href={`/classes/${cont.class_id}`} class="continue">
          <span class="continue-play"><Icon name="play" /></span>
          <span class="continue-text">
            <small>Pick up where you left off</small>
            <strong>{cont.title}</strong>
            <span>{cont.batch_name}{cont.class_date ? ` · ${dateShort(cont.class_date)}` : ''}</span>
          </span>
          <Icon name="next" />
        </Link>
      ) : null}
      <Section title={me.role === 'student' ? 'Your batches' : 'Batches'} id="h-batches">
        {regular.length ? (
          <div class="batch-grid">{regular.map((b) => <BatchCard b={b} me={me} key={b.id} />)}</div>
        ) : (
          <Empty icon="layers" title="No batches yet"
            action={me.role === 'admin'
              ? <Link class="btn btn-navy" href="/admin"><Icon name="shield" /><span>Open admin</span></Link>
              : <Button variant="line" icon="chat" href={waLink('Hi Vidyavedh Academy, I can’t see my batch in the learning app.')} target="_blank" rel="noopener">Message the academy</Button>}>
            {me.role === 'admin' ? 'Connect your Batches folder in Admin to bring in your classes.' : 'Once the academy adds you to a batch, its classes appear here.'}
          </Empty>
        )}
      </Section>
      {revision.length ? (
        <Section title="Revision kits" id="h-revision">
          <div class="batch-grid">{revision.map((b) => <BatchCard b={b} me={me} key={b.id} />)}</div>
        </Section>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Batch */
function ClassRow({ c }) {
  const tile = dateTile(c.class_date);
  const bits = [];
  if (c.videos) bits.push(c.videos > 1 ? `${c.videos} recordings` : 'Recording');
  if (c.notes) bits.push(c.notes > 1 ? `${c.notes} notes` : 'Notes');
  if (c.others) bits.push(plural(c.others, 'file'));
  return (
    <li>
      <Link href={`/classes/${c.id}`} class={`class-row${c.opened_at ? ' is-opened' : ''}`}>
        {c.is_loose ? (
          <span class="date-tile date-tile-icon"><Icon name="folder" /></span>
        ) : tile ? (
          <span class="date-tile"><strong>{tile.day}</strong><small>{tile.month} {tile.year}</small></span>
        ) : (
          <span class="date-tile date-tile-icon"><Icon name="book" /></span>
        )}
        <span class="class-row-text">
          <strong>{c.title}</strong>
          <small>{bits.join(' · ') || 'No files yet'}</small>
        </span>
        {c.opened_at ? <span class="opened" title="Opened"><Icon name="done" label="Opened" /></span> : null}
        <Icon name="next" class="go" />
      </Link>
    </li>
  );
}

export function Batch({ id }) {
  const { me } = useApp();
  const { data, error, loading, reload } = useRpc('get_batch', { p_batch_id: id }, [id]);
  const [q, setQ] = useState('');
  const [newestFirst, setNewestFirst] = useState(false);
  const classes = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    let list = data.classes.filter((c) => !term || c.title.toLowerCase().includes(term) || (c.folder_name || '').toLowerCase().includes(term));
    if (newestFirst) list = [...list].reverse();
    return list;
  }, [data, q, newestFirst]);
  const b = data && data.batch;
  return (
    <Shell backTo="/" backLabel="Home">
      {loading && !data ? <Loading rows={6} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {b ? (
        <>
          <div class="batch-head">
            <span class="subject subject-lg">{b.subject || '•'}</span>
            <div>
              <h1 class="page-title">{b.name}</h1>
              <p class="muted">{plural(b.class_count, 'class', 'classes')}{b.teacher ? ` · Taught by ${b.teacher}` : ''}{b.access_until ? ` · Access until ${dateShort(b.access_until)}` : ''}</p>
            </div>
          </div>
          {b.class_count ? (
            <div class="batch-progress">
              <Progress value={b.opened_count} max={b.class_count} label="Classes opened" />
              <span>{b.opened_count} of {b.class_count} classes opened</span>
            </div>
          ) : null}
          <div class="row">
            {me.role === 'student'
              ? <Link class="btn btn-line btn-sm" href="/progress"><Icon name="chart" /><span>Your progress</span></Link>
              : <Link class="btn btn-line btn-sm" href={`/batches/${b.id}/progress`}><Icon name="chart" /><span>Students’ progress</span></Link>}
          </div>
          {data.classes.length > 5 ? (
            <div class="toolbar">
              <label class="search">
                <Icon name="search" />
                <span class="sr-only">Search classes</span>
                <input type="search" value={q} placeholder="Search topics" onInput={(e) => setQ(e.currentTarget.value)} />
              </label>
              <Button variant="ghost" size="sm" icon="sort" onClick={() => setNewestFirst((v) => !v)} aria-pressed={newestFirst ? 'true' : 'false'}>
                {newestFirst ? 'Newest first' : 'Oldest first'}
              </Button>
            </div>
          ) : null}
          {classes.length ? (
            <ul class="class-list">{classes.map((c) => <ClassRow c={c} key={c.id} />)}</ul>
          ) : (
            <Empty icon="search" title={q ? 'No classes match' : 'No classes yet'}>
              {q ? 'Try another word from the topic.' : 'Recordings appear here as soon as the academy uploads them.'}
            </Empty>
          )}
        </>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Class */
function Recording({ file, newTab, index, total }) {
  const label = total > 1 ? `Recording ${index + 1} of ${total}` : 'Recording';
  const embed = msUrl(file.embed_url);
  const link = msUrl(file.web_url);
  if (!newTab && embed) {
    return (
      <div class="recording">
        <div class="player">
          <iframe src={embed} title={`${label}: ${file.name}`} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen referrerpolicy="strict-origin-when-cross-origin" loading="lazy" />
        </div>
        <p class="recording-alt">
          {file.duration_ms ? <span><Icon name="clock" />{duration(file.duration_ms)}</span> : null}
          {link ? <a href={link} target="_blank" rel="noopener">Not playing? Open it in a new tab <Icon name="external" /></a> : null}
        </p>
      </div>
    );
  }
  if (!link) {
    return <Notice tone="warn" icon="alert">{label} isn&rsquo;t available right now. Please tell the academy.</Notice>;
  }
  return (
    <a class="watch" href={link} target="_blank" rel="noopener">
      <span class="watch-play"><Icon name="play" /></span>
      <span class="watch-text">
        <strong>Watch the {label.toLowerCase()}</strong>
        <small>{file.duration_ms ? `${duration(file.duration_ms)} · ` : ''}Opens in Microsoft&rsquo;s player</small>
      </span>
      <Icon name="external" />
    </a>
  );
}

function FileLink({ file, icon, name, note }) {
  const link = msUrl(file.web_url);
  const body = (
    <>
      <span class="file-icon"><Icon name={icon} /></span>
      <span class="file-text"><strong>{name}</strong><small>{link ? note : 'Not available right now'}</small></span>
      {link ? <Icon name="external" /> : null}
    </>
  );
  return link ? <a class="file" href={link} target="_blank" rel="noopener">{body}</a> : <span class="file is-off">{body}</span>;
}

function ClassTests({ classId }) {
  const { data } = useRpc('my_quizzes', { p_class_id: classId }, [classId]);
  if (!data || !data.quizzes.length) return null;
  return (
    <Section title="Tests for this class" id="c-tests">
      <ul class="list test-list">{data.quizzes.map((z) => <TestCard z={z} key={z.id} />)}</ul>
    </Section>
  );
}

export function ClassPage({ id }) {
  const { home, me } = useApp();
  const { data, error, loading, reload } = useRpc('get_class', { p_class_id: id }, [id]);
  const marked = useRef(null);
  useEffect(() => {
    if (data && marked.current !== id) {
      marked.current = id;
      rpc('mark_opened', { p_class_id: id }).catch(() => {});
    }
  }, [data, id]);
  const mode = (home && home.settings && home.settings.playback_mode) || 'auto';
  const newTab = opensInNewTab(mode);
  const videos = data ? data.files.filter((f) => f.kind === 'video') : [];
  const notes = data ? data.files.filter((f) => f.kind === 'pdf') : [];
  const others = data ? data.files.filter((f) => f.kind === 'other') : [];
  const c = data && data.class;
  return (
    <Shell backTo={data ? `/batches/${data.batch.id}` : '/'} backLabel={data ? data.batch.name : 'Back'}>
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {c ? (
        <article class="class-page">
          <p class="eyebrow">{data.batch.subject ? `${data.batch.subject} · ` : ''}{c.is_loose ? 'Batch material' : `Class ${c.position} of ${data.batch.class_count}`}</p>
          <h1 class="page-title">{c.title}</h1>
          {c.class_date ? <p class="muted">{dateLong(c.class_date)}</p> : null}

          {videos.length ? videos.map((f, i) => <Recording key={f.id} file={f} newTab={newTab} index={i} total={videos.length} />) : null}

          {notes.length ? (
            <Section title="Notes" id="c-notes">
              <ul class="file-list">
                {notes.map((f) => (
                  <li key={f.id}>
                    <FileLink file={f} icon="file" name={f.name.replace(/\.pdf$/i, '')} note="PDF · opens in a new tab" />
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {others.length ? (
            <Section title="Other files" id="c-files">
              <ul class="file-list">
                {others.map((f) => (
                  <li key={f.id}>
                    <FileLink file={f} icon="folder" name={f.name} note="Opens in a new tab" />
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {!data.files.length ? <Empty icon="video" title="Nothing here yet">The recording appears here once it has been uploaded.</Empty> : null}

          <p class="fine"><Icon name="info" />Recordings and notes open with your academy Microsoft account. Downloads are switched off.</p>

          {me && me.role === 'student' ? <ClassTests classId={c.id} /> : (
            <Link class="ask-link" href={`/tests?new=1&batch=${data.batch.id}&class=${c.id}`}>
              <Icon name="tests" /><span><strong>Make a test for this class</strong><small>From questions in the bank</small></span><Icon name="next" />
            </Link>
          )}

          {me && me.role === 'student' ? (
            <Link class="ask-link" href={`/doubts?ask=1&batch=${data.batch.id}&class=${c.id}`}>
              <Icon name="doubt" /><span><strong>Stuck on something from this class?</strong><small>Ask your teacher a doubt</small></span><Icon name="next" />
            </Link>
          ) : null}

          {data.prev || data.next ? (
            <nav class="pager" aria-label="Other classes">
              {data.prev ? (
                <Link class="pager-link" href={`/classes/${data.prev.id}`}><Icon name="prev" /><span><small>Previous</small>{data.prev.title}</span></Link>
              ) : <span />}
              {data.next ? (
                <Link class="pager-link pager-next" href={`/classes/${data.next.id}`}><span><small>Next</small>{data.next.title}</span><Icon name="next" /></Link>
              ) : null}
            </nav>
          ) : null}
        </article>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Profile */
export function Profile() {
  const { me, home, installPrompt, clearInstallPrompt, doSignOut } = useApp();
  const [busy, setBusy] = useState(false);
  const standalone = isStandalone();
  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice.catch(() => null);
    clearInstallPrompt();
  };
  const roleLabel = { student: 'Student', teacher: 'Teacher', admin: 'Admin', parent: 'Parent' }[me.role] || me.role;
  const parents = (home && home.parents_linked) || 0;
  return (
    <Shell title="Profile">
      <div class="card profile">
        <span class="avatar" aria-hidden="true">{firstName(me).slice(0, 1).toUpperCase()}</span>
        <div>
          <h2>{me.full_name || me.email}</h2>
          <p class="muted">{me.email.replace('@', '@\u200b')}</p>
          <Chip tone="navy">{roleLabel}</Chip>
        </div>
      </div>

      <Section title="Install the app" id="p-install">
        {standalone ? (
          <Notice tone="ok" icon="done">You&rsquo;re using the installed app.</Notice>
        ) : installPrompt ? (
          <div class="card">
            <p>Add Vidyavedh to your home screen so it opens like any other app.</p>
            <Button variant="gold" icon="download" onClick={install}>Install the app</Button>
          </div>
        ) : isIOS() ? (
          <div class="card">
            <ol class="steps">
              <li>Open this page in <strong>Safari</strong>.</li>
              <li>Tap <strong>Share</strong> <Icon name="share" />, then <strong>Add to Home Screen</strong>.</li>
              <li>Open <strong>Vidyavedh</strong> from your home screen.</li>
            </ol>
          </div>
        ) : (
          <div class="card"><p>In Chrome or Edge, open the browser menu and choose <strong>Install app</strong> or <strong>Add to home screen</strong>.</p></div>
        )}
      </Section>

      {me.role === 'student' ? (
        <Section title="Your progress" id="p-progress">
          <Link class="tile" href="/progress"><Icon name="chart" /><span><strong>My progress</strong><small>Attendance, recordings, tests and mocks</small></span><Icon name="next" /></Link>
        </Section>
      ) : null}
      {me.role === 'student' ? <FeesLink /> : null}
      {me.role === 'student' && parents ? (
        <Section title="Parents" id="p-parents">
          <Link class="tile" href="/sharing"><Icon name="family" /><span><strong>Sharing with parents</strong><small>{parents === 1 ? '1 parent in the app' : `${parents} parents in the app`}: who sees your progress</small></span><Icon name="next" /></Link>
        </Section>
      ) : null}

      <Section title={me.role === 'parent' ? 'Alerts' : 'Reminders'} id="p-reminders">
        <Reminders parent={me.role === 'parent'} />
      </Section>

      <Section title="Privacy" id="p-privacy">
        <Link class="tile" href="/privacy"><Icon name="notice" /><span><strong>Privacy notice</strong><small>What the app keeps about students and parents, and why</small></span><Icon name="next" /></Link>
      </Section>

      <Section title="Help" id="p-help">
        <div class="card">
          <p>Questions about your classes or access? We reply on WhatsApp.</p>
          <Button variant="line" icon="chat" href={waLink('Hi Vidyavedh Academy, I have a question about the learning app.')} target="_blank" rel="noopener">Message the academy</Button>
        </div>
      </Section>

      <Button variant="ghost" icon="logout" busy={busy} onClick={async () => { setBusy(true); await doSignOut(); }}>Sign out</Button>
      <p class="fine version">Version {config.version}</p>
    </Shell>
  );
}

function Reminders({ parent }) {
  const toast = useToast();
  const support = pushSupport();
  const [on, setOn] = useState(null); // null while checking
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    currentSubscription().then((sub) => { if (live) setOn(Boolean(sub) && permission() === 'granted'); });
    return () => { live = false; };
  }, []);
  if (support === 'ios-install') {
    return <Notice icon="info">On iPhone and iPad, add the app to your Home Screen first (see above), open it from there, then turn on {parent ? 'alerts' : 'reminders'} here.</Notice>;
  }
  if (support !== 'ok') {
    return <Notice icon="info">This browser can&rsquo;t show notifications. Use Chrome on Android or a laptop, or the app from your iPhone&rsquo;s Home Screen.</Notice>;
  }
  const turnOn = async () => {
    setBusy(true); setErr(null);
    try { await enableNotifications(); setOn(true); toast(parent ? 'Alerts are on for this device' : 'Reminders are on for this device'); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const turnOff = async () => {
    setBusy(true); setErr(null);
    try { await disableNotifications(); setOn(false); toast(parent ? 'Alerts are off for this device' : 'Reminders are off for this device'); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div class="card">
      {on ? (
        <>
          {parent
            ? <p><strong>Alerts are on for this device.</strong> You get a notification when your child is marked absent or misses a compulsory test.</p>
            : <p><strong>Reminders are on for this device.</strong> You get a notification 30 minutes before each class, before a compulsory test you haven’t done closes, and when the academy posts an update.</p>}
          <Button variant="line" icon="belloff" busy={busy} onClick={turnOff}>Turn off</Button>
        </>
      ) : (
        <>
          <p>{parent ? 'Get a notification on this device when your child is marked absent or misses a compulsory test.' : 'Get a notification 30 minutes before each class, before a compulsory test you haven’t done closes, and when the academy posts an update.'}</p>
          <Button variant="gold" icon="bell" busy={busy} disabled={on === null} onClick={turnOn}>{parent ? 'Turn on alerts' : 'Turn on reminders'}</Button>
        </>
      )}
      {!on && permission() === 'denied' ? <Notice tone="warn" icon="alert">Notifications are blocked for this app. Allow them in your phone&rsquo;s settings, then come back.</Notice> : null}
      {err ? <ErrorBox error={{ message: err }} /> : null}
    </div>
  );
}

export function NotFound() {
  return (
    <Shell backTo="/" backLabel="Home">
      <Empty icon="search" title="Page not found" action={<Link class="btn btn-navy" href="/"><span>Go home</span></Link>}>
        That link may be old, or the class may have moved.
      </Empty>
    </Shell>
  );
}
